<language-reconciliation-modal>
    <dialog class="modal" ref="dialog">
        <div class="modal-box">
            <form method="dialog">
                <button class="absolute end-2 top-2 cursor-pointer text-xl"
                        aria-label="{locale?.t('close')}">
                    <i class="icon-clear"></i>
                </button>
            </form>

            <h3 class="font-bold text-lg mb-4 text-primary">
                {locale?.t('language_reconciliation_title') || 'Language Preference Mismatch'}
            </h3>

            <p class="mb-4">
                {getFallbackMessage()}
            </p>

            <div class="modal-action">
                <form method="dialog" class="flex gap-2">
                    <button class="btn text-primary rounded-lg" onclick="{handleNo}">
                        {locale?.t('language_reconciliation_no') || 'No, keep current'}
                    </button>
                    <button type="button" class="btn btn-primary rounded-lg" onclick="{handleYes}">
                        {locale?.t('language_reconciliation_yes') || 'Yes, update organization'}
                    </button>
                </form>
            </div>
        </div>

        <form method="dialog" class="modal-backdrop">
            <button>close</button>
        </form>
    </dialog>

    <script>
        import { getCustomerService } from '../../../services/service-loader';

        const DISMISS_DAYS = 14;

        export default {
            state: {
                userLanguage: null,
                customerLanguage: null,
                userLanguageName: null,
                customerLanguageName: null,
                customerId: null,
                userUuid: null
            },

            _getDismissKey() {
                return `lang-reconciliation-dismissed-${this.state.userUuid || 'unknown'}`;
            },

            _isDismissedRecently() {
                const dismissed = localStorage.getItem(this._getDismissKey());
                if (!dismissed) return false;
                const daysSince = (Date.now() - parseInt(dismissed, 10)) / (1000 * 60 * 60 * 24);
                return daysSince < DISMISS_DAYS;
            },

            _markDismissed() {
                localStorage.setItem(this._getDismissKey(), Date.now().toString());
            },

            async show(mismatchData) {
                console.log('[LanguageReconciliationModal] Showing with data:', mismatchData);

                // Get user UUID for dismiss tracking
                try {
                    const userContext = await this.tools.getUserContext();
                    this.state.userUuid = userContext?.userUuid;
                } catch (e) {
                    console.warn('[LanguageReconciliationModal] Could not get userUuid:', e);
                }

                // Skip if dismissed recently
                if (this._isDismissedRecently()) {
                    console.log('[LanguageReconciliationModal] Dismissed recently, skipping');
                    return;
                }

                // Store mismatch data
                this.state.userLanguage = mismatchData.userLanguage;
                this.state.customerLanguage = mismatchData.customerLanguage;
                this.state.userLanguageName = mismatchData.userLanguageName;
                this.state.customerLanguageName = mismatchData.customerLanguageName;

                // Get customer ID from store
                const { store } = await import('../../../services/store');
                this.state.customerId = store.getState().customer?.customerId;

                this.update();
                this.$('dialog').showModal();
            },

            close() {
                this.$('dialog').close();
            },

            getFallbackMessage() {
                // Try locale service first
                if (locale?.t) {
                    const translated = locale.t('language_reconciliation_message', {
                        userLanguage: this.state.userLanguageName,
                        customerLanguage: this.state.customerLanguageName
                    });
                    // Check if translation actually exists (not just returning the key)
                    if (translated && translated !== 'language_reconciliation_message') {
                        return translated;
                    }
                }

                // Fallback to English message
                return 'Your selected language (' + this.state.userLanguageName + ') differs from your organization\'s default (' + this.state.customerLanguageName + '). Would you like to update the organization default to ' + this.state.userLanguageName + '?';
            },

            handleNo() {
                console.log('[LanguageReconciliationModal] User chose to keep current default');
                this._markDismissed();
                this.close();
            },

            async handleYes() {
                console.log('[LanguageReconciliationModal] User chose to update organization default');

                try {
                    // Update customer's default language
                    const customerService = await getCustomerService();
                    await customerService.update(this.state.customerId, {
                        defaultLanguage: this.state.userLanguage
                    });

                    console.log('[LanguageReconciliationModal] Successfully updated customer default language');

                    // Update Redux store
                    const { store } = await import('../../../services/store');
                    const currentCustomer = store.getState().customer;
                    store.dispatch({
                        type: 'setCustomer',
                        payload: {
                            ...currentCustomer,
                            defaultLanguage: this.state.userLanguage
                        }
                    });

                    // Clear user language preference since it now matches customer default
                    const { getLocaleService } = await import('../../../services/service-loader');
                    const localeService = await getLocaleService();
                    localeService.clearUserLanguagePreference();
                    console.log('[LanguageReconciliationModal] Cleared user language preference (now matches org default)');

                    // Close modal
                    this.close();

                    // Reload page to refresh navbar (language selector should now be hidden)
                    window.location.reload();
                } catch (error) {
                    console.error('[LanguageReconciliationModal] Failed to update customer default language:', error);

                    // Show error message
                    const { getModalService } = await import('../../../services/service-loader');
                    const modalService = await getModalService();
                    modalService.show(
                        locale?.t('error') || 'Error',
                        locale?.t('language_reconciliation_error') || 'Failed to update organization language. Please try again.'
                    );
                }
            },

            onMounted() {
                // Make globally accessible
                window.languageReconciliationModal = this;

                this.$('dialog').addEventListener('close', () => {
                    this.onModalClosed();
                });
            },

            onModalClosed() {
                // Mark as dismissed when closed via backdrop or X button
                this._markDismissed();

                // Reset state
                this.state.userLanguage = null;
                this.state.customerLanguage = null;
                this.state.userLanguageName = null;
                this.state.customerLanguageName = null;
                this.state.customerId = null;
                this.update();
            }
        }
    </script>
</language-reconciliation-modal>
